import React from 'react'
import Plot from 'react-plotly.js';

const getColorForIndex = (index) => {
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#a05195', '#d45087', '#f95d6a', '#ff7c43'];
  return colors[index % colors.length];
};

const ElectrochemicalGraphs = ({ graphData }) => {
  const getMinMaxValues = (data, keys) => {
    let min = Infinity;
    let max = -Infinity;
    data.forEach(item => {
      keys.forEach(key => {
        // Only consider non-zero values for min/max calculation
        if (item[key] && item[key] !== 0 && item[key] < min) min = item[key];
        if (item[key] && item[key] !== 0 && item[key] > max) max = item[key];
      });
    });
    return [min, max];
  };

  const [minOnset, maxOnset] = getMinMaxValues(graphData, [
    "onset_potential(+/-0.01_mA/cm2)",
    "onset_potential(+/-0.05_mA/cm2)",
    "onset_potential(+/-0.1_mA/cm2)"
  ]);

  const createPlotData = () => {
    const keys = [
      "onset_potential(+/-0.01_mA/cm2)",
      "onset_potential(+/-0.05_mA/cm2)",
      "onset_potential(+/-0.1_mA/cm2)"
    ];

    return keys.map((key, index) => {
      // Create arrays for x and y values, handling null values for disconnected points
      const points = graphData.map((item, idx) => {
        // Return null for y when the value is 0 or missing
        // This creates a gap in the line
        const yValue = item[key] === 0 ? null : item[key];
        return { 
          x: item.compound, 
          y: yValue 
        };
      });

      // Filter out points where y is null for marker display only
      const visiblePoints = points.filter(point => point.y !== null);

      return {
        x: points.map(p => p.x),
        y: points.map(p => p.y),
        type: 'scatter',
        mode: 'lines+markers',
        connectgaps: false, // This is the key setting that prevents connecting across null values
        name: key.replace("onset_potential", "").replace(/_/g, ' ').trim(),
        line: { color: getColorForIndex(index), width: 2 },
        marker: { 
          size: 6,
          // We need to make sure markers don't appear where data is null/0
          // This is handled by the points filtering above
        }
      };
    });
  };

  // Calculate the range for better centering
  const yRange = maxOnset - minOnset;
  const yPadding = yRange * 0.1; // 10% padding
  const yMin = Math.max(0, minOnset - yPadding); // Ensure yMin is not negative
  const yMax = maxOnset + yPadding;

  const layout = {
    title: { text: 'ORR/OER performance', font: { size: 25, weight: 'bold', color: '#333' } },
    xaxis: { 
      title: 'Material',
      tickangle: 45,
      automargin: true,
      tickmode: 'array',
      tickvals: graphData.map((_, index) => index),
      ticktext: graphData.map(item => item.compound),
      range: [-0.5, graphData.length - 0.5] // Add padding to x-axis
    },
    yaxis: { 
      title: 'Onset potential (V vs. RHE)',
      tickformat: '.2f',
      range: [yMin, yMax]
    },
    legend: {
      x: 1,
      y: 1,
      xanchor: 'left',
      yanchor: 'top',
      bgcolor: 'rgba(255,255,255,0.6)',
      bordercolor: '#ccc',
      borderwidth: 1
    },
    hovermode: 'closest',
    margin: { l: 60, r: 30, t: 50, b: 100 },
    autosize: false,
    width: 800, // Set a fixed width
    height: 500, // Set a fixed height
    plot_bgcolor: '#fff',
    paper_bgcolor: '#fff',
  };

  const config = {
    responsive: true,
    displayModeBar: 'hover',
    modeBarButtonsToAdd: ['zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d'],
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png',
      filename: 'electrochemical_testing_export',
      height: 600,
      width: 800,
      scale: 10
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-xl font-bold mb-4">Electrochemical testing</h3>
      <div className="flex justify-center">
        <div className="w-[70%] bg-white p-4 rounded-lg shadow-md">
          <Plot
            data={createPlotData()}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '500px' }}
            useResizeHandler={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ElectrochemicalGraphs;